import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Paperclip, X } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export const QuoteForm = ({
  content,
  initialProduct,
  sourcePage = "homepage",
  submitLabel = "Request my quote",
  embedded = false,
}) => {
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    delivery_country: "", postcode: "",
    product_type: initialProduct || content.products[0]?.id || "butt-joint",
    diameter: "help", product_dims: "",
    quantity: content.moq, delivery_date: "",
    finishes: [], message: "", consent: false,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [artworks, setArtworks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const onStyle = (e) => set("product_type", e.detail);
    window.addEventListener("ptd:quote-style", onStyle);
    return () => window.removeEventListener("ptd:quote-style", onStyle);
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleFinish = (id) =>
    set("finishes", form.finishes.includes(id) ? form.finishes.filter((x) => x !== id) : [...form.finishes, id]);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} is over 25 MB.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await axios.post(`${API}/artwork`, fd);
        setArtworks((a) => [...a, data]);
      } catch (err) {
        toast.error(formatDetail(err.response?.data?.detail) || `Could not upload ${f.name}.`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name,
        email: form.email,
        company: form.company,
        delivery_country: form.delivery_country,
        postcode: form.postcode,
        product_type: form.product_type,
        diameter_mm: form.diameter === "help" ? null : Number(form.diameter),
        help_choosing: form.diameter === "help",
        product_dims: form.product_dims,
        quantity: Number(form.quantity),
        delivery_date: form.delivery_date,
        finishes: form.finishes,
        message: form.message,
        consent: form.consent,
        source_page: sourcePage,
        artworks: artworks.map(({ id, filename, size }) => ({ id, filename, size })),
      });
      setSent(true);
      toast.success("Quote request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-[#0E1116] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-[#F5F3EF] placeholder:text-[#666F7D] transition-colors duration-300";
  const labelCls = "block font-mono-spec text-[10px] uppercase tracking-[0.22em] text-[#666F7D] mb-2";

  const formCard = sent ? (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="h-full min-h-[420px] border border-amber-500/25 bg-[#14171D] rounded-2xl flex flex-col items-center justify-center text-center p-10"
      data-testid="quote-success"
    >
      <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-amber-500/30 flex items-center justify-center">
        <Check size={28} className="text-[#D4AF37]" />
      </div>
      <h3 className="mt-7 font-serif-display text-2xl text-[#F5F3EF]">Request received.</h3>
      <p className="mt-3 text-sm text-[#A1A8B3] max-w-sm leading-relaxed">
        Thank you — your specification is with our studio. Expect a tailored quote in your inbox soon.
      </p>
      <button
        onClick={() => setSent(false)}
        data-testid="quote-another-btn"
        className="mt-8 ghost-btn border border-white/15 text-sm text-[#F5F3EF] px-6 py-3 rounded-full"
      >
        Send another request
      </button>
    </motion.div>
  ) : (
    <form onSubmit={submit} className="border border-white/8 bg-[#14171D] rounded-2xl p-7 sm:p-9" data-testid="quote-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="q-style">Tube style</label>
          <select id="q-style" value={form.product_type} onChange={(e) => set("product_type", e.target.value)} className={inputCls} data-testid="quote-product-select">
            {content.products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="q-diameter">Diameter (internal)</label>
          <select id="q-diameter" value={form.diameter} onChange={(e) => set("diameter", e.target.value)} className={inputCls} data-testid="quote-diameter-select">
            <option value="help">I need help choosing</option>
            {content.sizes.map((d) => (
              <option key={d} value={d}>Ø {d} mm</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="q-dims">Product dimensions or required internal dimensions (mm)</label>
          <input id="q-dims" value={form.product_dims} onChange={(e) => set("product_dims", e.target.value)} className={inputCls} placeholder="e.g. product is 75 mm wide × 140 mm tall" data-testid="quote-dims-input" />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-qty">Quantity (min {content.moq})</label>
          <input id="q-qty" type="number" min={content.moq} step="50" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputCls} data-testid="quote-quantity-input" required />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-date">Required delivery date (optional)</label>
          <input id="q-date" type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="quote-date-input" />
        </div>
      </div>

      <div className="mt-6">
        <label className={labelCls}>Preferred finish (optional)</label>
        <div className="flex flex-wrap gap-2" data-testid="quote-finishes-group">
          {content.finishes.map((f) => {
            const active = form.finishes.includes(f.id);
            return (
              <button
                type="button"
                key={f.id}
                onClick={() => toggleFinish(f.id)}
                data-testid={`quote-finish-${f.id}`}
                className={`text-[12px] font-medium rounded-full px-4 py-2 border transition-all duration-300 ${
                  active ? "border-amber-500/60 bg-[#D4AF37]/12 text-[#E6CA65]" : "border-white/10 text-[#A1A8B3] hover:border-white/25"
                }`}
              >
                {f.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <label className={labelCls}>Artwork files (optional)</label>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".pdf,.ai,.png,.jpg,.jpeg,.eps,.svg,.zip,.psd,.tif,.tiff"
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="quote-artwork-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="quote-artwork-btn"
          className="ghost-btn border border-dashed border-white/20 rounded-xl px-5 py-3.5 text-[13px] text-[#A1A8B3] w-full flex items-center justify-center gap-2"
        >
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
          {uploading ? "Uploading…" : "Attach print artwork (PDF, AI, PNG, JPG — max 25 MB)"}
        </button>
        {artworks.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="quote-artwork-list">
            {artworks.map((a) => (
              <span key={a.id} className="flex items-center gap-2 border border-amber-500/25 bg-[#0E1116] rounded-full px-3.5 py-1.5 text-[12px] text-[#D9A76A]" data-testid={`quote-artwork-${a.id}`}>
                {a.filename}
                <button type="button" onClick={() => setArtworks((xs) => xs.filter((x) => x.id !== a.id))} className="text-[#666F7D] hover:text-red-300" aria-label={`Remove ${a.filename}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-6">
        <div>
          <label className={labelCls} htmlFor="q-name">Your name *</label>
          <input id="q-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Jane Appleton" required data-testid="quote-name-input" />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-company">Business name</label>
          <input id="q-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} placeholder="Brand Ltd" data-testid="quote-company-input" />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-email">Email *</label>
          <input id="q-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="jane@brand.com" required data-testid="quote-email-input" />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-country">Delivery country</label>
          <input id="q-country" value={form.delivery_country} onChange={(e) => set("delivery_country", e.target.value)} className={inputCls} placeholder="United Kingdom" data-testid="quote-country-input" />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-postcode">Postcode</label>
          <input id="q-postcode" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className={inputCls} placeholder="SW1A 1AA" data-testid="quote-postcode-input" />
        </div>
      </div>

      <div className="mt-6">
        <label className={labelCls} htmlFor="q-notes">Project notes</label>
        <textarea id="q-notes" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className={`${inputCls} resize-none`} placeholder="Artwork status, deadlines, anything else we should know…" data-testid="quote-message-input" />
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer select-none" data-testid="quote-consent-label">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => set("consent", e.target.checked)}
          required
          className="mt-0.5 w-4 h-4 accent-[#D4AF37]"
          data-testid="quote-consent-checkbox"
        />
        <span className="text-[12px] text-[#A1A8B3] leading-relaxed">
          I consent to Tube Boxes Direct storing these details and any attached files so the team can respond to my enquiry. *
        </span>
      </label>

      <button
        type="submit"
        disabled={sending || uploading}
        data-testid="quote-submit-btn"
        className="gold-btn mt-8 w-full text-[#0B0D10] font-semibold text-sm py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
        {sending ? "Sending…" : submitLabel}
      </button>
      <p className="mt-4 text-center text-[11px] text-[#666F7D]">No spam, no obligation — just a considered quote.</p>
    </form>
  );

  if (embedded) {
    return <div className="max-w-3xl mx-auto" data-testid="quote-embedded">{formCard}</div>;
  }

  return (
    <section id="quote" className="relative py-28 sm:py-36 bg-[#0E1116] border-y border-white/5" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-14 lg:grid-cols-[1fr_1.2fr]">
        <Reveal>
          <Eyebrow>Start your tube project</Eyebrow>
          <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-5xl font-medium tracking-tight text-[#F5F3EF] leading-[1.15]">
            Tell us the spec.<br />We'll send the <span className="foil-text italic">numbers.</span>
          </h2>
          <p className="mt-6 text-base text-[#A1A8B3] font-light leading-relaxed max-w-md">
            Share the essentials and we'll come back with pricing and practical advice. Minimum order {content.moq} units — artwork guidance included, always.
          </p>
          <div className="mt-10 space-y-4">
            {["Custom pricing from our quoting team", "Free artwork & template advice", `Production dispatch from ${content.lead_time_weeks} weeks after artwork approval`, "Delivery time quoted separately for your address"].map((line) => (
              <div key={line} className="flex items-center gap-3 text-sm text-[#A1A8B3]">
                <span className="w-5 h-5 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
                  <Check size={11} className="text-[#D4AF37]" />
                </span>
                {line}
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-[#666F7D]">
            Prefer email? <a href={`mailto:${content.contact_email}`} className="text-[#D9A76A] hover:text-[#E6CA65]" data-testid="quote-email-link">{content.contact_email}</a>
          </p>
        </Reveal>
        <Reveal delay={0.15}>{formCard}</Reveal>
      </div>
    </section>
  );
};
